/**
 * Stripe Product カタログ
 *
 * Stripe の Product / Price ID は環境ごとに異なるため, DB (CleanStripePrice) に保持する.
 * ここにはメタデータのみ定義する.
 *
 * 構造: Product > Feature (1:1) > Price
 * Feature lookup_key を唯一の Entitlement 識別子として扱う.
 *
 * Prefix 規約: 各機能ドメインが独自 prefix を持つ (platform_, clean_, 将来 binder_ など).
 * xxx_base は機能ドメインの基本料金を指す.
 */
export const STRIPE_PRODUCTS = {
  /** プラットフォーム基本料: 全 CleanTenant が必ず持つ必須 Item. ¥0. */
  PLATFORM: {
    ID: 'platform_base',
    NAME: 'プラットフォーム基本料',
    DESCRIPTION: 'AssistBnB プラットフォーム利用の基本料金',
    FEATURE: {
      NAME: 'platform_base',
    },
    PRICE: {
      LOOKUP_KEY: 'platform_base_standard_v1',
      CURRENCY: 'jpy',
      INTERVAL: 'month' as const,
      BILLING_SCHEME: 'per_unit' as const,
      USAGE_TYPE: 'licensed' as const,
      UNIT_AMOUNT: 0,
    },
  },
  /** 清掃管理: 物件数ベース Tiered. */
  CLEAN: {
    ID: 'clean_base',
    NAME: '清掃管理',
    DESCRIPTION:
      '物件数に応じた清掃管理機能. 100部屋まで¥1,000/件, 101部屋以降¥500/件',
    FEATURE: {
      NAME: 'clean_base',
    },
    PRICE: {
      LOOKUP_KEY: 'clean_base_standard_v1',
      CURRENCY: 'jpy',
      INTERVAL: 'month' as const,
      BILLING_SCHEME: 'tiered' as const,
      USAGE_TYPE: 'licensed' as const,
      TIERS_MODE: 'graduated' as const,
      TIERS: [
        { UP_TO: 100, UNIT_AMOUNT: 1000 },
        { UP_TO: 'inf' as const, UNIT_AMOUNT: 500 },
      ],
    },
  },
  /** LINE 通知: Billing Meter による従量課金. */
  LINE_NOTIFY: {
    ID: 'clean_line_notify',
    NAME: 'LINE通知',
    DESCRIPTION: 'LINE通知の従量課金',
    FEATURE: {
      NAME: 'clean_line_notify',
    },
    METER: {
      EVENT_NAME: 'clean_line_notify',
      DISPLAY_NAME: 'LINE通知',
    },
    PRICE: {
      LOOKUP_KEY: 'clean_line_notify_standard_v1',
      CURRENCY: 'jpy',
      INTERVAL: 'month' as const,
      BILLING_SCHEME: 'per_unit' as const,
      USAGE_TYPE: 'metered' as const,
      UNIT_AMOUNT: 3,
    },
  },
  /** 宿泊者台帳: 物件数ベース Tiered (~10 ¥1,000 / ~20 ¥900 / ~30 ¥800 / ~40 ¥700 / ~50 ¥600 / 51+ ¥500). */
  BINDER: {
    ID: 'binder_base',
    NAME: '宿泊者台帳',
    DESCRIPTION: 'ゲスト情報収集・宿泊者名簿 CSV 出力 (旅館業法対応)',
    FEATURE: {
      NAME: 'binder_base',
    },
    PRICE: {
      LOOKUP_KEY: 'binder_base_standard_v1',
      CURRENCY: 'jpy',
      INTERVAL: 'month' as const,
      BILLING_SCHEME: 'tiered' as const,
      USAGE_TYPE: 'licensed' as const,
      TIERS_MODE: 'graduated' as const,
      TIERS: [
        { UP_TO: 10, UNIT_AMOUNT: 1000 },
        { UP_TO: 20, UNIT_AMOUNT: 900 },
        { UP_TO: 30, UNIT_AMOUNT: 800 },
        { UP_TO: 40, UNIT_AMOUNT: 700 },
        { UP_TO: 50, UNIT_AMOUNT: 600 },
        { UP_TO: 'inf' as const, UNIT_AMOUNT: 500 },
      ],
    },
  },
  /**
   * メッセージAI (chatbot): ゲストメッセージへの AI 返信ドラフト生成・自動一次返信.
   * 部屋数ベース Tiered (binder_base と同型). quantity = 課金対象 (enabled) の ChatbotRoomUnit 数 (#4326).
   *
   * 価格 (DR-001 確定): ¥8,000 開始, 10 部屋ごとに ¥400 ずつ割引, 101 部屋目以降 ¥4,000 で底打ち.
   * graduated (段階累積) なので各物件は属する区間の単価で課金され, 合計が請求額になる
   * (例: 100 部屋 = ¥620,000/月, 実効 ¥6,200/件. ¥4,000 は 101 件目以降の限界単価).
   */
  CHATBOT: {
    ID: 'chatbot_base',
    NAME: 'メッセージAI',
    DESCRIPTION: 'ゲストメッセージへの AI 返信ドラフト生成・自動一次返信',
    FEATURE: {
      NAME: 'chatbot_base',
    },
    PRICE: {
      LOOKUP_KEY: 'chatbot_base_standard_v1',
      CURRENCY: 'jpy',
      INTERVAL: 'month' as const,
      BILLING_SCHEME: 'tiered' as const,
      USAGE_TYPE: 'licensed' as const,
      TIERS_MODE: 'graduated' as const,
      TIERS: [
        { UP_TO: 10, UNIT_AMOUNT: 8000 },
        { UP_TO: 20, UNIT_AMOUNT: 7600 },
        { UP_TO: 30, UNIT_AMOUNT: 7200 },
        { UP_TO: 40, UNIT_AMOUNT: 6800 },
        { UP_TO: 50, UNIT_AMOUNT: 6400 },
        { UP_TO: 60, UNIT_AMOUNT: 6000 },
        { UP_TO: 70, UNIT_AMOUNT: 5600 },
        { UP_TO: 80, UNIT_AMOUNT: 5200 },
        { UP_TO: 90, UNIT_AMOUNT: 4800 },
        { UP_TO: 100, UNIT_AMOUNT: 4400 },
        { UP_TO: 'inf' as const, UNIT_AMOUNT: 4000 },
      ],
    },
  },
  /**
   * 帯表示 (BandView): 清掃カレンダーの帯 (ガント) 表示.
   * Price はテナント別の交渉額でアドホック作成するため, ここには固定 UNIT_AMOUNT / LOOKUP_KEY を持たせない.
   * 共通メタ (currency / interval / scheme) のみ定義し, 実額は activateCustomPricedFeature がカスタム Price として作る.
   */
  BAND_VIEW: {
    ID: 'clean_band_view',
    NAME: '帯表示',
    DESCRIPTION: '清掃カレンダーの帯 (ガント) 表示',
    FEATURE: {
      NAME: 'clean_band_view',
    },
    PRICE: {
      CURRENCY: 'jpy',
      INTERVAL: 'month' as const,
      BILLING_SCHEME: 'per_unit' as const,
      USAGE_TYPE: 'licensed' as const,
      IS_CUSTOM_PRICE: true,
    },
  },
  /**
   * 清掃時間レポート: 物件別・スタッフ別の清掃時間分析 (/clean/cleaner/analytics/staff-times).
   * BAND_VIEW と同じくテナント別の交渉額で課金するため固定 UNIT_AMOUNT / LOOKUP_KEY を持たせない.
   */
  TIME_REPORT: {
    ID: 'clean_time_report',
    NAME: '清掃時間レポート',
    DESCRIPTION: '物件別・スタッフ別の清掃時間分析レポート',
    FEATURE: {
      NAME: 'clean_time_report',
    },
    PRICE: {
      CURRENCY: 'jpy',
      INTERVAL: 'month' as const,
      BILLING_SCHEME: 'per_unit' as const,
      USAGE_TYPE: 'licensed' as const,
      IS_CUSTOM_PRICE: true,
    },
  },
} as const

type StripeProduct = (typeof STRIPE_PRODUCTS)[keyof typeof STRIPE_PRODUCTS]

/**
 * テナント別カスタム価格 (IS_CUSTOM_PRICE: true) の Product のみのユニオン.
 * activateCustomPricedFeature が固定価格 Product を受け取らないよう型レベルで制約する.
 */
export type CustomPricedStripeProduct = Extract<
  StripeProduct,
  { PRICE: { IS_CUSTOM_PRICE: true } }
>

/** カスタム価格 Product の一覧 (コマンドの --product 解決用). */
export const CUSTOM_PRICED_STRIPE_PRODUCTS = Object.values(
  STRIPE_PRODUCTS
).filter(
  (product): product is CustomPricedStripeProduct =>
    'IS_CUSTOM_PRICE' in product.PRICE && product.PRICE.IS_CUSTOM_PRICE === true
)
